import React, { useEffect, useState } from 'react';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import Typography from '@visa/vds/typography';
import Grid from '@visa/vds/grid';
import Row from '@visa/vds/row';
import Col from '@visa/vds/col';
import BillingReviewTable from './Components/BillingReviewTable';
import BillingReviewInfo from './Components/BillingReviewInfo';
import MCSOverlayLoading from '../../../components/MCSOverlayLoading';
import MCSConfirmDialog from '../../../components/MCSConfirmDialog';
import { GlobalAlertContext } from '../../../context/GlobalAlertContext';
import { BillingApi } from '../../../services/billing.service';
import { PageHelper } from '../../../_helpers/PageHelper';
import { ObjectMapHelper } from '../../../_helpers/ObjectMapHelper';
import { FileHelper } from '../../../_helpers/FileHelper';
import { billingReviewApproveDto, billingReviewApproveItemDto, billingDownloadFileDto } from '../../../_mappings/Billing';
import BillingReviewButtonPanel from './Components/BillingReviewButtonPanel';

const BillingReview = () => {
    const { promiseInProgress } = usePromiseTracker();
    const [error, setError] = useState(null);
    const [selectedPage, setSelectedPage] = useState(1);
    const [allTableData, setAllTableData] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 5;
    const [response, setResponse] = useState(null);
    const [panelActions, setPanelActions] = useState({
        allowApprove: false,
        allowDownload: false
    });
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [showFeeWaiver, setShowFeeWaiver] = useState(false);

    const viewReview = async () => {
        trackPromise(
            BillingApi.ViewBillingReview()
                .then((result) => {
                    if (!result.data) {
                        setError('Unable to view billing.');
                        return;
                    } else if (!result.data) {
                        setError(result.data);
                        return;
                    }

                    setPageState(result.data);
                })
                .catch(error => {
                    setError(error.message);
                })
        );
    }

    const confirmApproveBilling = () => {
        let confirmMessage = "Are you sure you want to approve the billing for {0} file?".replace("{0}", response.fileVersion);
        setConfirmDialog({
            ...confirmDialog,
            title: "Confirm",
            type: "warning",
            message: confirmMessage,
            open: true,
            id: "billing-review-approve-confirm",
        });
    }

    const approveConfirmHandler = async (id, alertObj) => {
        let approveDto = mapToApproveDto();
        trackPromise(
            BillingApi.ApproveBilling(approveDto)
                .then((result) => {
                    if (!result.data) {
                        alertObj.setErrorMessage('Billing is not approved');
                    } else if (result.status !== 200) {
                        alertObj.setErrorMessage(result.data.error.message);
                    } else {
                        alertObj.setSuccessMessage('Billing has been approved');
                        setPageState(result.data);
                    }
                })
                .catch((error) => {
                    alertObj.setErrorMessage('An error occured while approving billing : ' + error.message);
                })
        );
    }

    const downloadBillingFile = async (alertObj) => {
        var dto = ObjectMapHelper.MapDto(response, billingDownloadFileDto);
        trackPromise(
            BillingApi.DownloadBillingFile(dto)
                .then(response => {
                    FileHelper.DownloadFile(response);
                })
                .catch((error) => {
                    alertObj.setErrorMessage('An error occured while download billing file : ' + error.message);
                })
        );
    }

    const setPageState = (result) => {
        setResponse(result);
        setPanelActions({
            allowApprove: result.allowApprove,
            allowDownload: result.approved
        });
        setAllTableData(result.items);
        setSelectedPage(1);
        setCurrentTableData(1, result.items);
        setTotalCount(result.itemsCount);
        setTotalPages(PageHelper.GetTotalCountToPages(pageSize, result.itemsCount));
    }

    function mapToApproveDto() {
        var dto = ObjectMapHelper.MapDto(response, billingReviewApproveDto);
        var items = response.items.map((row, key) => {
            let item = { ...billingReviewApproveItemDto };
            return ObjectMapHelper.MapDto(row, item);
        });

        dto.items = items;
        return dto;
    }

    useEffect(() => {
        viewReview();
    }, [])

    const handlePageChange = (newPage) => {
        setSelectedPage(newPage);
        setCurrentTableData(newPage, allTableData);
    };

    const setCurrentTableData = (page, all) => {
        let skipCount = PageHelper.GetSkipCount(pageSize, page);
        if (skipCount > all.length)
            return;

        let temp = all.slice(skipCount, skipCount + pageSize);
        let _showFeeWaiver = false;
        temp.forEach(row => {
            if(row.productSubscriptionInfo.feeWaiverId){
                _showFeeWaiver=true;
            }
        });
        setShowFeeWaiver(_showFeeWaiver)
        setTableData(temp);
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <>
            {promiseInProgress && <MCSOverlayLoading></MCSOverlayLoading>}
            <GlobalAlertContext.Consumer>
                {
                    alertObj => (
                        <>
                            {confirmDialog && (
                                <MCSConfirmDialog
                                    alertObj={alertObj}
                                    confirmHandler={approveConfirmHandler}
                                    confirmDialog={confirmDialog}
                                    setConfirmDialog={setConfirmDialog}
                                />
                            )}
                            <Grid>
                                <Row>
                                    <Col>
                                        <Typography tag="h1">Billing Sign Off</Typography>
                                    </Col>
                                </Row>
                                {response && tableData &&
                                    <>
                                        <Row>
                                            <Col>
                                                <BillingReviewInfo billingSummary={response} />
                                            </Col>
                                        </Row>
                                        <BillingReviewTable
                                            selectedPage={selectedPage}
                                            handlePageChange={handlePageChange}
                                            totalPages={totalPages}
                                            pageSize={pageSize}
                                            totalCount={totalCount}
                                            tableData={tableData}
                                            showFeeWaiver = {showFeeWaiver}
                                        />
                                        <BillingReviewButtonPanel
                                            panelActions={panelActions}
                                            onApproveClick={() => confirmApproveBilling()}
                                            onDownloadClick={() => downloadBillingFile(alertObj)}
                                        />
                                    </>
                                }
                            </Grid>
                        </>
                    )
                }
            </GlobalAlertContext.Consumer>
        </>
    );
}

export default BillingReview;

